// apps/web/src/features/kronos/timer/UnifiedTrackerForm.tsx
import { useCallback, useEffect, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Collapse,
  FormControlLabel,
  IconButton,
  Paper,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  Alert,
} from '@mui/material';
import TimerIcon from '@mui/icons-material/Timer';
import EditNoteIcon from '@mui/icons-material/EditNote';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  buildEntryTimes,
  formatDurationSeconds,
  toDateInput,
  validateTimeEntryForm,
  type TimeEntryFormValues,
} from '../timeEntries';
import { formatDuration } from '../reportFormat';
import { type DesignationOption, encodeExistingOptionId } from '../designationOptions';
import type { TaskSuggestion } from '../taskSuggestions';
import type { AutoStopPreset } from './timerMachine';
import { useTypewriter } from './useTypewriter';
import { useActiveTags } from '../useKronosData';
import { colors } from '../../../core/theme/theme';

// ─── Types ─────────────────────────────────────────────────────────

export interface TrackerProjectOption {
  id: string;
  code: string;
  name: string;
  /**
   * Whether the user already belongs to the project — drives the
   * "My projects" / "Other projects" Autocomplete grouping. Logging
   * time on an unassigned project auto-adds the user server-side.
   */
  assigned?: boolean;
}

export type TrackerDesignationOption = DesignationOption;

type FormMode = 'timer' | 'manual';

interface RecentProject {
  id: string;
  label: string;
  designationId: string;
}

export interface UnifiedTrackerFormProps {
  projects: TrackerProjectOption[];
  designations: TrackerDesignationOption[];
  projectsLoading: boolean;
  designationsLoading: boolean;
  recentProjects: RecentProject[];
  /** Description autocomplete options, pre-scoped to the selected project by the parent. */
  taskSuggestions?: TaskSuggestion[];
  /** Whether a timer is currently active (running or paused). */
  timerActive: boolean;
  /** Description from the live timer (synced in real-time). */
  timerDescription: string;
  onProjectIdChange: (projectId: string) => void;
  onManualSubmit: (payload: {
    values: TimeEntryFormValues;
    times: { startTime: Date; endTime: Date; durationSeconds: number };
  }) => Promise<void>;
  onTimerStart: (params: {
    projectId: string;
    designationId: string;
    autoStopPreset: AutoStopPreset;
    customAutoStopAt?: string;
    tags: string[];
  }) => void;
  onTimerDescriptionChange: (description: string) => void;
  /**
   * Continue a recent project as a fresh timer (auto-saves any running
   * timer first, handled by the parent). When omitted, the ▷ button is
   * not rendered.
   */
  onContinueRecent?: (rp: RecentProject) => void;
}

// ─── Auto-stop presets ─────────────────────────────────────────────

const PRESETS: { key: AutoStopPreset; labelKey: string }[] = [
  { key: 'none', labelKey: 'kronos.timer.autoStop.none' },
  { key: 'lunch', labelKey: 'kronos.timer.autoStop.lunch' },
  { key: 'end-of-day', labelKey: 'kronos.timer.autoStop.endOfDay' },
  { key: 'in-1h', labelKey: 'kronos.timer.autoStop.inHours' },
  { key: 'in-2h', labelKey: 'kronos.timer.autoStop.inHours' },
  { key: 'in-3h', labelKey: 'kronos.timer.autoStop.inHours' },
];

function presetCount(key: AutoStopPreset): number | undefined {
  const match = key.match(/^in-(\d+)h$/);
  return match ? Number(match[1]) : undefined;
}

/**
 * Defaults computed at call time so today's date is always correct.
 */
function emptyValues(): TimeEntryFormValues {
  return {
    projectId: '',
    designationId: '',
    description: '',
    date: toDateInput(new Date()),
    mode: 'duration',
    startHHmm: '09:00',
    endHHmm: '',
    durationHours: '1',
    tags: [],
    billable: true,
  };
}

// ─── Component ─────────────────────────────────────────────────────

/**
 * Unified tracker form that merges timer start + manual entry into
 * one form with a mode toggle. Shared fields (project, designation,
 * description) appear once — only the bottom section swaps between
 * timer presets and manual date/duration fields.
 */
/**
 * Animated title that typewriter-erases and retypes the suffix when mode changes.
 * "What " is persistent; only the rest animates.
 */
function TypewriterTitle({ mode }: { mode: FormMode }) {
  const { t } = useTranslation();
  const suffix = mode === 'timer'
    ? t('kronos.tracker.titleSuffixTimer')
    : t('kronos.tracker.titleSuffixManual');
  const { text, showCursor } = useTypewriter(t('kronos.tracker.titlePrefix'), suffix, 20);

  return (
    <Typography variant="h4" sx={{ fontWeight: 600, minHeight: '1.3em' }}>
      {text}
      <Box
        component="span"
        sx={{
          display: 'inline-block',
          width: '2px',
          height: '1em',
          bgcolor: colors.bodyText,
          ml: 0.25,
          verticalAlign: 'text-bottom',
          opacity: showCursor ? 1 : 0,
          animation: showCursor ? 'none' : undefined,
          ...(showCursor && {
            animation: 'cursorBlink 0.8s step-end infinite',
          }),
          '@keyframes cursorBlink': {
            '0%, 100%': { opacity: 1 },
            '50%': { opacity: 0 },
          },
        }}
      />
    </Typography>
  );
}

export default function UnifiedTrackerForm({
  projects,
  designations,
  projectsLoading,
  designationsLoading,
  recentProjects,
  taskSuggestions = [],
  timerActive,
  timerDescription,
  onProjectIdChange,
  onManualSubmit,
  onTimerStart,
  onTimerDescriptionChange,
  onContinueRecent,
}: UnifiedTrackerFormProps) {
  const { t } = useTranslation();
  const [formMode, setFormMode] = useState<FormMode>('timer');
  const [autoStop, setAutoStop] = useState<AutoStopPreset>('none');

  const {
    control,
    register,
    handleSubmit,
    watch,
    setValue,
    setError,
    clearErrors,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<TimeEntryFormValues>({
    defaultValues: emptyValues(),
  });

  const selectedProjectId = watch('projectId');
  const selectedDesignationId = watch('designationId');
  const manualMode = watch('mode');
  const descriptionValue = watch('description');
  const tagsValue = watch('tags');
  const tagOptions = useActiveTags();

  // Report project changes upward so the parent can swap the
  // designations subscription.
  useEffect(() => {
    onProjectIdChange(selectedProjectId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProjectId]);

  // A stale designation is cleared where the intent actually lives — the
  // project picker's onChange. It used to be an effect on `designations`,
  // but `useProjectDesignations` returns a fresh array every render, so the
  // effect fired constantly and wiped any programmatic pre-fill (quick pick,
  // description suggestion) before the new project's designations loaded.

  // When timer is active, sync description changes back to the timer.
  useEffect(() => {
    if (timerActive && descriptionValue !== timerDescription) {
      onTimerDescriptionChange(descriptionValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [descriptionValue, timerActive]);

  // When the timer becomes active, populate the description from timer state.
  useEffect(() => {
    if (timerActive && timerDescription) {
      setValue('description', timerDescription);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timerActive]);

  // ── Quick pick handler ───────────────────────────────────────────
  const handleQuickPick = useCallback(
    (projectId: string, designationId: string) => {
      setValue('projectId', projectId);
      // Pre-fill designation with encoded id (options use 'existing:' prefix)
      setValue('designationId', encodeExistingOptionId(designationId));
      onProjectIdChange(projectId);
    },
    [setValue, onProjectIdChange],
  );

  // ── Timer start ──────────────────────────────────────────────────
  const handleTimerStart = useCallback(() => {
    if (!selectedProjectId || !selectedDesignationId) return;
    onTimerStart({
      projectId: selectedProjectId,
      designationId: selectedDesignationId,
      autoStopPreset: autoStop,
      tags: tagsValue,
    });
  }, [selectedProjectId, selectedDesignationId, autoStop, tagsValue, onTimerStart]);

  // ── Manual submit ────────────────────────────────────────────────
  const onFormSubmit = handleSubmit(async (values) => {
    clearErrors();
    const validation = validateTimeEntryForm(values);
    let hasError = false;
    (Object.keys(validation) as Array<keyof TimeEntryFormValues>).forEach((field) => {
      const message = validation[field];
      if (message) {
        setError(field, { message });
        hasError = true;
      }
    });
    if (hasError) return;

    const times = buildEntryTimes(values);
    if ('error' in times) {
      setError('durationHours', { message: t('kronos.tracker.submitFailed') });
      return;
    }

    try {
      await onManualSubmit({ values, times });
      // Reset form on success
      reset(emptyValues());
    } catch (err) {
      const message =
        err instanceof Error && err.message
          ? err.message
          : t('kronos.tracker.submitFailed');
      setError('root', { message });
    }
  });

  const canStartTimer = selectedProjectId !== '' && selectedDesignationId !== '' && !timerActive;

  const rootError = (errors as { root?: { message?: string } }).root?.message;

  // Memoised designation options (already pre-built by parent)
  const designationOptions = designations;

  // Outer tinted container background
  const outerBg = colors.peach;

  return (
    <Paper
      variant="outlined"
      sx={{
        position: 'relative',
        overflow: 'hidden',
        bgcolor: outerBg,
        borderColor: colors.lightGray,
        p: { xs: 2, sm: 3 },
        mb: 3,
      }}
      data-testid="unified-tracker-form"
    >
      {/* Brand watermark */}
      <Box
        component="img"
        src="/images/brand-watermark.png"
        alt=""
        sx={{
          position: 'absolute',
          bottom: -40,
          right: -30,
          width: 280,
          height: 280,
          opacity: 0.04,
          pointerEvents: 'none',
          transform: 'rotate(-15deg)',
        }}
      />

      <Stack spacing={2.5} sx={{ position: 'relative', zIndex: 1 }}>
        {/* Header row: title + mode toggle */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 1,
          }}
        >
          <TypewriterTitle mode={formMode} />
          <ToggleButtonGroup
            value={formMode}
            exclusive
            onChange={(_, v) => v && setFormMode(v)}
            size="small"
            aria-label={t('kronos.tracker.modeToggleAria')}
            sx={{
              '& .MuiToggleButton-root': {
                px: 2,
                py: 0.75,
                fontWeight: 700,
                fontSize: 13,
              },
              '& .MuiToggleButton-root.Mui-selected': {
                bgcolor: colors.darkBg,
                color: colors.white,
                '&:hover': { bgcolor: '#2a2d33' },
              },
            }}
          >
            <ToggleButton value="timer" data-testid="mode-timer">
              <TimerIcon sx={{ fontSize: 16, mr: 0.5 }} />
              {t('kronos.tracker.modeTimer')}
            </ToggleButton>
            <ToggleButton value="manual" data-testid="mode-manual">
              <EditNoteIcon sx={{ fontSize: 16, mr: 0.5 }} />
              {t('kronos.tracker.modeManual')}
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {/* Quick picks: recent projects */}
        {recentProjects.length > 0 && (
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 0.75, display: 'block' }}>
              {t('kronos.tracker.quickPickRecent')}
            </Typography>
            <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
              {recentProjects.map((rp) => (
                <Box
                  key={rp.id}
                  sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}
                >
                  <Chip
                    icon={<FolderOpenIcon sx={{ fontSize: 14 }} />}
                    label={rp.label}
                    variant="outlined"
                    onClick={() => handleQuickPick(rp.id, rp.designationId)}
                    sx={{
                      borderStyle: 'dashed',
                      borderColor: colors.textMuted,
                      color: colors.bodyText,
                      fontWeight: 600,
                      fontSize: 12,
                      cursor: 'pointer',
                      '&:hover': { bgcolor: colors.white },
                      ...(selectedProjectId === rp.id && {
                        borderStyle: 'solid',
                        borderColor: colors.red,
                        bgcolor: colors.white,
                      }),
                    }}
                  />
                  {onContinueRecent && (
                    <IconButton
                      size="small"
                      aria-label={t('kronos.entries.continueTimer')}
                      onClick={() => onContinueRecent(rp)}
                      data-testid={`recent-continue-${rp.id}`}
                    >
                      <PlayArrowIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  )}
                </Box>
              ))}
            </Stack>
          </Box>
        )}

        {/* Inner form card — white for contrast against peach outer */}
        <Paper
          variant="outlined"
          component="form"
          onSubmit={onFormSubmit}
          noValidate
          sx={{
            bgcolor: colors.white,
            borderColor: colors.lightGray,
            p: { xs: 2, sm: 2.5 },
          }}
          data-testid="tracker-form"
        >
          <Stack spacing={2}>
            {rootError && (
              <Alert severity="error" data-testid="tracker-root-error">
                {rootError}
              </Alert>
            )}

            {!projectsLoading && projects.length === 0 && (
              <Alert severity="info" data-testid="tracker-no-projects">
                {t('kronos.tracker.noProjectsForUser')}
              </Alert>
            )}

            {/* Row: Project + Designation */}
            <Box sx={{ display: 'flex', gap: 2, flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
              <Controller
                control={control}
                name="projectId"
                render={({ field }) => {
                  const selected = projects.find((p) => p.id === field.value) ?? null;
                  return (
                    <Autocomplete
                      disabled={projectsLoading}
                      options={projects}
                      value={selected}
                      onChange={(_, v) => {
                        field.onChange(v?.id ?? '');
                        // The designation belongs to the old project.
                        setValue('designationId', '');
                      }}
                      getOptionLabel={(opt) => `${opt.code} — ${opt.name}`}
                      groupBy={(opt) =>
                        opt.assigned === false
                          ? t('kronos.tracker.otherProjects')
                          : t('kronos.tracker.myProjects')
                      }
                      isOptionEqualToValue={(opt, v) => opt.id === v.id}
                      sx={{ flex: 1, minWidth: 200 }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={t('kronos.tracker.project')}
                          required
                          placeholder={t('kronos.tracker.projectPlaceholder')}
                          error={Boolean(errors.projectId)}
                          helperText={errors.projectId?.message}
                          inputRef={field.ref}
                          data-testid="tracker-project"
                        />
                      )}
                    />
                  );
                }}
              />

              <Controller
                control={control}
                name="designationId"
                render={({ field }) => {
                  const selected = designationOptions.find((d) => d.id === field.value) ?? null;
                  const disabled = !selectedProjectId || designationsLoading;
                  return (
                    <Autocomplete
                      disabled={disabled}
                      options={designationOptions}
                      value={selected}
                      onChange={(_, v) => field.onChange(v?.id ?? '')}
                      getOptionLabel={(opt) => opt.label}
                      groupBy={(opt) => opt.groupLabel}
                      isOptionEqualToValue={(opt, v) => opt.id === v.id}
                      renderOption={(optionProps, opt) => (
                        <Box
                          component="li"
                          {...optionProps}
                          key={opt.id}
                          sx={{ justifyContent: 'space-between !important', gap: 1 }}
                        >
                          <Typography variant="body2" component="span">
                            {opt.label}
                          </Typography>
                          {opt.recent && (
                            <Chip
                              label={t('kronos.tracker.designationRecentBadge')}
                              size="small"
                              sx={{
                                height: 18,
                                fontSize: '0.65rem',
                                bgcolor: colors.yellow,
                                color: '#000',
                                '& .MuiChip-label': { px: 0.75 },
                              }}
                            />
                          )}
                        </Box>
                      )}
                      sx={{ flex: 1, minWidth: 200 }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={t('kronos.tracker.designation')}
                          required
                          placeholder={t('kronos.tracker.designationPlaceholder')}
                          error={Boolean(errors.designationId)}
                          helperText={errors.designationId?.message}
                          inputRef={field.ref}
                          data-testid="tracker-designation"
                        />
                      )}
                    />
                  );
                }}
              />
            </Box>

            {/* Description (shared between both modes) — freeSolo
                autocomplete fed by the user's past tasks. With no project
                selected it lists the last tasks across every project
                (Clockify-style); picking one back-fills project,
                designation and duration. `openOnFocus` so a click on the
                empty field is enough to see the list. */}
            <Controller
              control={control}
              name="description"
              render={({ field }) => (
                <Autocomplete<TaskSuggestion, false, false, true>
                  freeSolo
                  openOnFocus
                  options={taskSuggestions}
                  inputValue={field.value ?? ''}
                  onInputChange={(_, v) => field.onChange(v)}
                  filterOptions={(opts, state) => {
                    const needle = state.inputValue.trim().toLowerCase();
                    if (!needle) return opts;
                    return opts.filter((o) => o.label.toLowerCase().includes(needle));
                  }}
                  getOptionLabel={(opt) => (typeof opt === 'string' ? opt : opt.label)}
                  isOptionEqualToValue={(opt, val) =>
                    typeof opt !== 'string' &&
                    typeof val !== 'string' &&
                    opt.projectId === val.projectId &&
                    opt.label === val.label &&
                    opt.designationId === val.designationId
                  }
                  onChange={(_, value) => {
                    if (!value || typeof value === 'string') return;
                    setValue('description', value.label);
                    setValue('projectId', value.projectId);
                    setValue('designationId', encodeExistingOptionId(value.designationId));
                    // "H:MM" — the format parseHoursInput accepts.
                    setValue('mode', 'duration');
                    setValue('durationHours', formatDurationSeconds(value.durationSeconds));
                    // The projectId watcher effect notifies the parent, which
                    // swaps the designation subscription.
                  }}
                  renderOption={(props, opt) => (
                    <Box
                      component="li"
                      {...props}
                      key={`${opt.projectId}|${opt.label}|${opt.designationId}`}
                    >
                      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <Typography variant="body2">{opt.label}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {opt.projectLabel} · {opt.designationLabel} ·{' '}
                          {formatDuration(opt.durationSeconds)}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t('kronos.tracker.description')}
                      placeholder={
                        formMode === 'timer'
                          ? t('kronos.timer.descriptionPlaceholderTimer')
                          : t('kronos.tracker.descriptionPlaceholder')
                      }
                      multiline
                      minRows={3}
                      maxRows={6}
                      error={Boolean(errors.description)}
                      helperText={errors.description?.message}
                      inputRef={field.ref}
                      sx={{ '& .MuiInputBase-input::placeholder': { fontStyle: 'italic' } }}
                      data-testid="tracker-description"
                    />
                  )}
                />
              )}
            />

            {/* ── Timer mode section ─────────────────────────────── */}
            <Collapse in={formMode === 'timer'} unmountOnExit>
              <Stack spacing={2}>
                {/* Auto-stop chips */}
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                    {t('kronos.timer.autoStop.label')}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    {PRESETS.map(({ key, labelKey }) => {
                      const count = presetCount(key);
                      const label = count !== undefined
                        ? t(labelKey, { count })
                        : t(labelKey);
                      return (
                        <Chip
                          key={key}
                          label={label}
                          onClick={() => setAutoStop(key)}
                          variant={autoStop === key ? 'filled' : 'outlined'}
                          sx={{
                            ...(autoStop === key && {
                              bgcolor: colors.yellow,
                              color: '#000',
                              '&:hover': { bgcolor: colors.yellowHover },
                            }),
                          }}
                        />
                      );
                    })}
                  </Box>
                </Box>

                {/* Tags — captured at start, editable again at stop */}
                <Controller
                  control={control}
                  name="tags"
                  render={({ field }) => (
                    <Autocomplete
                      multiple
                      options={[...new Set([...tagOptions, ...field.value])]}
                      value={field.value}
                      onChange={(_, v) => field.onChange(v)}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={t('kronos.tracker.tags')}
                          placeholder={t('kronos.tracker.tagsPlaceholder')}
                          data-testid="timer-tags"
                        />
                      )}
                    />
                  )}
                />

                {/* Start timer button */}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    variant="contained"
                    color="primary"
                    disabled={!canStartTimer}
                    onClick={handleTimerStart}
                    data-testid="timer-start-btn"
                  >
                    {t('kronos.timer.startTimer')}
                  </Button>
                </Box>
              </Stack>
            </Collapse>

            {/* ── Manual mode section ────────────────────────────── */}
            <Collapse in={formMode === 'manual'} unmountOnExit>
              <Stack spacing={2}>
                {/* Date + mode toggle + start/duration/end */}
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                  <TextField
                    label={t('kronos.tracker.date')}
                    type="date"
                    {...register('date')}
                    slotProps={{ inputLabel: { shrink: true } }}
                    error={Boolean(errors.date)}
                    helperText={errors.date?.message}
                    sx={{ minWidth: 160 }}
                    data-testid="tracker-date"
                  />

                  <TextField
                    label={t('kronos.tracker.startTime')}
                    type="time"
                    {...register('startHHmm')}
                    slotProps={{ inputLabel: { shrink: true } }}
                    error={Boolean(errors.startHHmm)}
                    helperText={errors.startHHmm?.message}
                    sx={{ minWidth: 130 }}
                    data-testid="tracker-start"
                  />

                  <Controller
                    control={control}
                    name="mode"
                    render={({ field }) => (
                      <ToggleButtonGroup
                        value={field.value}
                        exclusive
                        onChange={(_, v) => v && field.onChange(v)}
                        size="small"
                        color="primary"
                        aria-label={t('kronos.tracker.modeDuration')}
                        sx={{ height: 56, alignSelf: 'flex-start' }}
                        data-testid="tracker-mode"
                      >
                        <ToggleButton value="duration">
                          {t('kronos.tracker.modeDuration')}
                        </ToggleButton>
                        <ToggleButton value="range">
                          {t('kronos.tracker.modeRange')}
                        </ToggleButton>
                      </ToggleButtonGroup>
                    )}
                  />

                  {manualMode === 'range' ? (
                    <TextField
                      label={t('kronos.tracker.endTime')}
                      type="time"
                      {...register('endHHmm')}
                      slotProps={{ inputLabel: { shrink: true } }}
                      error={Boolean(errors.endHHmm)}
                      helperText={errors.endHHmm?.message}
                      sx={{ minWidth: 130 }}
                      data-testid="tracker-end"
                    />
                  ) : (
                    <TextField
                      label={t('kronos.tracker.duration')}
                      {...register('durationHours')}
                      error={Boolean(errors.durationHours)}
                      helperText={
                        errors.durationHours?.message ?? t('kronos.tracker.durationHelp')
                      }
                      sx={{ minWidth: 150 }}
                      data-testid="tracker-duration"
                    />
                  )}
                </Box>

                {/* Tags + billable row */}
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                  <Controller
                    control={control}
                    name="tags"
                    render={({ field }) => (
                      <Autocomplete
                        multiple
                        options={[...new Set([...tagOptions, ...field.value])]}
                        value={field.value}
                        onChange={(_, v) => field.onChange(v)}
                        sx={{ flex: 1, minWidth: 200 }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={t('kronos.tracker.tags')}
                            placeholder={t('kronos.tracker.tagsPlaceholder')}
                            data-testid="tracker-tags"
                          />
                        )}
                      />
                    )}
                  />

                  <Controller
                    control={control}
                    name="billable"
                    render={({ field }) => (
                      <FormControlLabel
                        control={
                          <Switch
                            checked={field.value}
                            onChange={(e) => field.onChange(e.target.checked)}
                            data-testid="tracker-billable"
                          />
                        }
                        label={t('kronos.tracker.billable')}
                        sx={{ flexShrink: 0 }}
                      />
                    )}
                  />
                </Box>

                {/* Save entry button */}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    disabled={isSubmitting || projects.length === 0}
                    data-testid="tracker-submit"
                  >
                    {t('kronos.tracker.save')}
                  </Button>
                </Box>
              </Stack>
            </Collapse>
          </Stack>
        </Paper>
      </Stack>
    </Paper>
  );
}
